import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, concatMap, firstValueFrom, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import {
    EntityFilters,
    IBlockReason,
    IExternalSortingModel,
    INamedBlockReason,
    INamedTaskType,
    IPaginatedTableResponse,
    IRole,
    ISortingModel,
    ISubjectType,
    ITask,
    ITaskPATCH,
    ITaskPOST,
    ITaskType,
    IUser,
    Pagination,
    PlannedTaskSortKey,
    TaskApiService,
    TaskAssignPermissions,
    TaskStatus,
    canAssignTask,
    isTaskAssignedToTeam,
} from 'shared';

import { ReportControlRoute } from '../../models/control-route.model';
import { EntityAction, FilterableEntityAction } from '../../models/entity-action.model';
import { EntityEvent } from '../../models/entity-event.action';
import { BlockReasonPopupComponent } from '../../mtp-main/components/modals/block-reason-popup/block-reason-popup.component';
import { EntitySharePopupComponent } from '../../mtp-main/components/modals/entity-share-popup/entity-share-popup.component';
import { RoleManagementService } from '../../mtp-settings/shared/services/role-management.service';
import { LazyTranslateLoader } from '../../utils/lazy-translate-loader';
import { FormSubmittedPopupComponent } from '../components/modals/form-submitted-popup/form-submitted-popup.component';
import { SnackbarComponent } from '../components/modals/snackbar/snackbar.component';
import { SurveyEntryPopupComponent } from '../components/modals/survey-entry-popup/survey-entry-popup.component';
import { UserSelectPopupComponent } from '../components/modals/user-select-popup/user-select-popup.component';
import { ActiveTaskService } from './active-task.service';
import { ControlBarService } from './control-bar.service';
import { DomComponent, DomService } from './dom.service';
import { FormSubmissionService } from './form-submission.service';
import { FormService } from './form.service';
import { POIService } from './poi.service';
import { SocketService } from './socket.service';
import { AppStateService } from './state';
import { TeamService } from './team.service';
import { TenantService } from './tenant.service';
import { UserTeamSelectorSidebarService } from './user-team-selector-sidebar.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class TaskService {
    private static readonly ASSIGN_POPUP_TARGET_PREFIX = 'AssignPopup_';

    private readonly _taskTypes = new BehaviorSubject<Array<INamedTaskType>>([]);

    public readonly taskTypes = this._taskTypes.asObservable();
    private readonly _blockReasons = new BehaviorSubject<Array<INamedBlockReason>>([]);

    private readonly _events = new Subject<EntityEvent<ITask>>();
    public readonly events = this._events.asObservable();

    public readonly blockReasons = this._blockReasons.asObservable();
    private _assignPopup: DomComponent<UserSelectPopupComponent>;
    private roles: Array<IRole>;

    private readonly tenantService = inject(TenantService);
    private readonly tasksApi = inject(TaskApiService);
    private readonly poiService = inject(POIService);
    private readonly socketService = inject(SocketService);
    private readonly userService = inject(UserService);
    private readonly teamService = inject(TeamService);
    private readonly roleService = inject(RoleManagementService);
    private readonly formService = inject(FormService);

    private readonly translate = inject(TranslateService);
    private readonly domService = inject(DomService);
    private readonly controlBarService = inject(ControlBarService);
    private readonly userTeamSelectorService = inject(UserTeamSelectorSidebarService);
    private readonly appState = inject(AppStateService);
    private readonly router = inject(Router);
    private readonly activeTaskService = inject(ActiveTaskService);

    private readonly destroyRef = inject(DestroyRef);
    private readonly formSubmissionService = inject(FormSubmissionService);

    private popupComponent: DomComponent<SurveyEntryPopupComponent>;

    public constructor() {
        // Clear data on logout
        this.appState.user.distinctStream.pipe(filter((user) => !user)).subscribe((_) => {
            this._taskTypes.next([]);
            this._blockReasons.next([]);
        });
        // Refresh on socket connect
        this.appState.socketStatus.distinctStream
            .pipe(
                filter((s) => s.status === 'CONNECTED'),
                // Wait for the user to be known
                switchMap(() =>
                    firstValueFrom(
                        this.appState.user.distinctStream.pipe(
                            filter((user) => !!user),
                            timeout(3000)
                        )
                    )
                ),
                // Refresh everything
                switchMap(() => Promise.all([this.refreshTaskTypes(), this.refreshBlockReasons()]))
            )
            .subscribe();

        // Update the active tasks service when a task is updated
        this.events.subscribe((event) => {
            switch (event.type) {
                case 'updated':
                    this.activeTaskService.onTaskUpdated(event.entity);
                    break;
                case 'removed':
                    this.activeTaskService.onTaskRemoved(event.entityId);
                    break;
            }
        });

        // Route assign-popup submissions to the relevant task by parsing the target
        this.userTeamSelectorService.selectionSubmitted
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(({ target }) => target.startsWith(TaskService.ASSIGN_POPUP_TARGET_PREFIX)),
                concatMap(({ target, userTeam }) =>
                    from(
                        this.assignTask(
                            target.substring(TaskService.ASSIGN_POPUP_TARGET_PREFIX.length),
                            userTeam?.user?.id,
                            userTeam?.team?.id
                        )
                    ).pipe(
                        catchError(() => {
                            console.error('Failed to assign task');
                            return of(null);
                        })
                    )
                )
            )
            .subscribe();
    }

    //
    // Data fetching
    //

    public async refreshTaskTypes(): Promise<Array<ITaskType>> {
        // Get task types
        const config = await firstValueFrom(this.tenantService.config);
        const taskTypes = config.taskApi.taskTypes;
        // Wait for base translations to have loaded
        await firstValueFrom((this.translate.currentLoader as LazyTranslateLoader).translationsLoaded$);
        // Extract translations and append them
        taskTypes.forEach((type) => this.applyTranslationsForTaskType(type));
        // Map to named type
        const namedTaskTypes = taskTypes.map(TaskService.mapTaskTypeToNamedType);
        // Publish task types and return them
        this._taskTypes.next(namedTaskTypes);
        return taskTypes;
    }

    public async refreshBlockReasons(): Promise<Array<IBlockReason>> {
        // Get block reasons
        const config = await firstValueFrom(this.tenantService.config.pipe(take(1)));
        const blockReasons = config.taskApi.blockReasons;
        // Extract translations and append them
        blockReasons
            .filter((type) => typeof type.name === 'object')
            .forEach((type) => {
                Object.entries(type.name).forEach(([lang, name]) => {
                    this.translate.setTranslation(lang, { blockReason: { [type.id]: name } }, true);
                });
            });
        // Map to named type
        const namedBlockReasons = blockReasons.map((it) => ({
            id: it.id,
            name: typeof it.name === 'object' ? `blockReason.${it.id}` : it.name,
        }));
        // Publish block reasons and return them
        this._blockReasons.next(namedBlockReasons);
        return blockReasons;
    }

    public async getDraftTasks(
        scheduleDate: number,
        sorting?: ISortingModel<ITask>,
        includePreview: boolean = true
    ): Promise<Array<ITask>> {
        // Check permission
        if (!(await this.userService.checkPermission('Tasks.ReadTasks.*'))) return [];
        // Fetch tasks
        return firstValueFrom(this.tasksApi.getPlannedTasks(true, false, includePreview, scheduleDate, sorting));
    }

    public async getDraftTasksPaginated(
        scheduleDate: number,
        pagination: Pagination,
        filters: EntityFilters,
        sorting: Array<IExternalSortingModel<PlannedTaskSortKey>>,
        includePreview: boolean = true
    ): Promise<IPaginatedTableResponse<ITask>> {
        // Check permission
        if (!(await this.userService.checkPermission('Tasks.ReadTasks.*'))) return { totalCount: 0, data: [] };
        // Fetch tasks
        return firstValueFrom(
            this.tasksApi.getPlannedTasksPaginated(
                pagination,
                filters,
                sorting,
                true,
                false,
                includePreview,
                scheduleDate
            )
        );
    }

    public getTaskFilters<TFilterModel>(): Observable<TFilterModel> {
        return this.tasksApi.getTaskFilters<TFilterModel>();
    }

    //
    // Utilities
    //

    public updateTaskTypes(newTypes: Array<ITaskType>): void {
        const newNamedTypes = newTypes.map((task) => {
            this.applyTranslationsForTaskType(task);
            return TaskService.mapTaskTypeToNamedType(task);
        });
        const newIds = newTypes.map((taskType) => taskType.id);
        this._taskTypes.next([...this._taskTypes.value.filter((type) => !newIds.includes(type.id)), ...newNamedTypes]);
    }

    public localUpdate(task: ITask): void {
        this._events.next({ type: 'updated', entity: task });
    }

    public localRemove(taskId: string): void {
        this._events.next({ type: 'removed', entityId: taskId });
    }

    public getTaskTypeForId(id: string): Observable<INamedTaskType> {
        return this.taskTypes.pipe(map((its) => its.find((it) => it.id === id)));
    }

    public getBlockReasonForId(id: string): Observable<INamedBlockReason> {
        return this.blockReasons.pipe(map((its) => its.find((it) => it.id === id)));
    }

    public getTaskTypeForIdSync(id: string): INamedTaskType {
        return this._taskTypes.value.find((it) => it.id === id);
    }

    public getBlockReasonForIdSync(id: string): INamedBlockReason {
        return this._blockReasons.value.find((it) => it.id === id);
    }

    public createTask(task: ITaskPOST): Observable<ITask> {
        return this.tasksApi.postTask(task).pipe(tap((t) => this.localUpdate(t)));
    }

    public updateTask(task: ITaskPATCH): Observable<ITask> {
        return this.tasksApi.patchTask(task.id, task).pipe(tap((t) => this.localUpdate(t)));
    }

    public async setLinkedIssue(taskId: string, issueId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.patchTask(taskId, { issueId }));
        if (!task.issueId) task.issueId = null; // Make removal explicit
        this.localUpdate(task);
        return task;
    }

    public removeTaskType(id: string): void {
        this._taskTypes.next(this._taskTypes.getValue().filter((type) => type.id !== id));
    }

    public buildActions = async (
        task: ITask,
        assignEnabled: boolean,
        updateTaskCallback?: (updatedTask: ITask) => void
    ): Promise<Array<EntityAction>> => {
        const actions: Array<EntityAction> = [];
        if (!task) return actions;

        // Dependencies
        const userId = this.appState.user.value?.id ?? null;

        // Cache the list of roles
        if (!this.roles)
            this.roleService.listRoles().then((result) => {
                this.roles = result;
            });

        // Order actions by priority
        return [
            await this.buildPickUpEntityAction(task, userId),
            await this.buildSubmitRequiredFormAction(task, updateTaskCallback),
            await this.buildFinishEntityAction(task, userId),
            await this.buildApproveInspectionEntityAction(task, userId),
            await this.buildRejectInspectionEntityAction(task, userId),
            await this.buildDropEntityAction(task, userId),
            await this.buildBlockEntityAction(task, userId),
            await this.buildCancelEntityAction(task),
            await this.buildUnblockEntityAction(task, userId),
            await this.buildResumeEntityAction(task, userId),
            await this.buildAssignEntityAction(task, assignEnabled),
            await this.buildAssignInspectionEntityAction(task),
            await this.buildShareEntityAction(task),
            await this.buildEditEntityAction(task),
        ].filter((a) => a.applicable);
    };

    public async assignTask(taskId: string, userId: string | null, teamId: string | null): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.assignTask(taskId, userId, teamId));

        // We need to explicitly set this, so localUpdate can overwrite the existing value.
        if (!userId) task.assigneeId = null;
        if (!teamId) task.teamId = null;

        this.localUpdate(task);
        return task;
    }

    public async pickUpTask(taskId: string): Promise<ITask> {
        if (this.hasActiveTaskSync())
            throw Error('Attempted to pick up a task while another was still active for the current user.');
        const task = await firstValueFrom(this.tasksApi.pickUpTask(taskId));
        this.localUpdate(task);
        return task;
    }

    public async finishTask(taskId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.finishTask(taskId));
        this.localUpdate(task);
        return task;
    }

    public async approveTaskInspection(taskId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.approveTaskInspection(taskId));
        this.localUpdate(task);
        return task;
    }

    public async rejectTaskInspection(taskId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.rejectTaskInspection(taskId));
        this.localUpdate(task);
        return task;
    }

    public async blockTask(taskId: string, blockReasonId?: string, blockReasonNote?: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.blockTask(taskId, blockReasonId, blockReasonNote));
        this.localUpdate(task);
        return task;
    }

    public async dropTask(taskId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.dropTask(taskId));
        this.localUpdate(task);
        return task;
    }

    public async fetchTask(id: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.getTask(id));
        this.localUpdate(task);
        return task;
    }

    public async cancelTask(id: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.cancelTask(id));
        this.localUpdate(task);
        return task;
    }

    public async unblockTask(id: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.unblockTask(id));
        this.localUpdate(task);
        return task;
    }

    public async resumeTask(id: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.resumeTask(id));
        this.localUpdate(task);
        return task;
    }

    public async deleteDraftTask(id: string): Promise<ITask> {
        return firstValueFrom(this.tasksApi.deleteTask(id));
    }

    public async setTaskType(id: string, type: ITaskType): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                typeId: type?.id,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async setSubjectType(id: string, type: ISubjectType): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                subjectTypeId: type?.id || null,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async setDescription(id: string, description: string): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                description,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async setInspectionRequired(id: string, inspectionRequired: boolean): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                inspectionRequired,
                inspectorId: inspectionRequired ? undefined : null,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async assignInspector(id: string, userId: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.assignInspector(id, userId));

        this.localUpdate(task);
        return task;
    }

    public async unassignInspector(id: string): Promise<ITask> {
        const task = await firstValueFrom(this.tasksApi.unassignInspector(id));

        this.localUpdate(task);
        return task;
    }

    public async setFinishBefore(id: string, date: number | null): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                finishBeforeDate: date,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async setPickupAfter(id: string, date: number | null): Promise<ITask> {
        const task = await firstValueFrom(
            this.tasksApi.patchTask(id, {
                pickupAfterDate: date,
            })
        );
        this.localUpdate(task);
        return task;
    }

    public async publishDraftTask(id: string): Promise<ITask> {
        return firstValueFrom(this.tasksApi.publishTask(id));
    }

    public hasActiveTaskSync(): boolean {
        return !!this.appState.activeTasksForUser.value.length;
    }

    public async navigateToSubmissionOrOpenForm(
        task: ITask,
        onSubmit: (updatedTask: ITask) => void,
        formId: string,
        submissionId?: string
    ): Promise<void> {
        // If we have a submission id, navigate to the existing submission
        if (submissionId) {
            await this.router.navigate(['/history/form-submissions/', submissionId]);
            return;
        }
        // Otherwise, we prompt the user to create a new submission
        const form = await this.formService.fetchForm(formId);
        if (!form) return;
        this.popupComponent?.remove();
        this.popupComponent = this.domService.appendComponentTo<SurveyEntryPopupComponent>(
            'overlay-container',
            SurveyEntryPopupComponent,
            {
                model: JSON.parse(form.model),
                formId,
            }
        );
        this.popupComponent.component.onClose
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                take(1),
                switchMap(async (result) => {
                    if (!result) return;
                    // Submit the form results
                    const submission = await this.formSubmissionService.submitForm(
                        formId,
                        JSON.stringify(result),
                        task.location,
                        { poiId: task.poiId, taskId: task.id }
                    );

                    const updatedTask = structuredClone(task);
                    // Immediately update the task with the new submission id
                    const formLink = (updatedTask.forms ?? []).find((f) => f.id === formId);
                    if (formLink) {
                        formLink.submissionId = submission.id;
                        this.localUpdate(updatedTask);
                        onSubmit(updatedTask);
                    }

                    // Open the success popup
                    FormSubmittedPopupComponent.show(this.domService, submission);
                })
            )
            .subscribe();
    }

    private applyTranslationsForTaskType(type: ITaskType): void {
        if (type.name && typeof type.name !== 'object') return;
        Object.entries(type.name).forEach(([lang, name]) => {
            this.translate.setTranslation(lang, { taskType: { [type.id]: name } }, true);
        });
    }

    private async buildBlockEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        return {
            label: 'comp.task-list-item.action.block',
            onClick: ($event): void => {
                if ($event) $event.stopImmediatePropagation();
                this.domService.appendComponentTo<BlockReasonPopupComponent>(
                    'overlay-container',
                    BlockReasonPopupComponent,
                    {
                        taskId: task.id,
                    }
                );
            },
            icon: 'remove_circle_outline',
            disabled: false,
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'IN_PROGRESS' && task.status !== 'TODO') return false;
                const permBlockAll = await this.userService.checkPermission('Tasks.BlockTasks.All');
                const permBlockSelf = await this.userService.checkPermission('Tasks.BlockTasks.Self');
                return permBlockAll || (assignedToSelf && permBlockSelf);
            })(),
        };
    }

    private async buildCancelEntityAction(task: ITask): Promise<FilterableEntityAction> {
        return {
            label: 'comp.task-list-item.action.cancel',
            onClick: async ($event): Promise<void> => {
                if ($event) $event.stopImmediatePropagation();
                await this.cancelTask(task.id);
                if (
                    await firstValueFrom(
                        this.controlBarService.controlRoute.pipe(map((route) => route.mode !== 'DETAIL'))
                    )
                ) {
                    this.showStatusChangeNotification('CANCEL');
                }
            },
            icon: 'icon-cancel',
            iconStyle: 'outlined',
            disabled: false,
            applicable:
                (await this.userService.checkPermission('Tasks.CancelTasks')) &&
                (task.status === 'BLOCKED' || task.status === 'TODO' || task.status === 'IN_PROGRESS'),
        };
    }

    private async buildUnblockEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        return {
            label: 'comp.task-list-item.action.unblock',
            onClick: async ($event): Promise<void> => {
                if ($event) $event.stopImmediatePropagation();
                await this.unblockTask(task.id);
            },
            icon: 'do_not_disturb_off',
            iconStyle: 'outlined',
            disabled: false,
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'BLOCKED') return false;
                const permUnblockAll = await this.userService.checkPermission('Tasks.UnblockTasks.All');
                const permUnblockSelf = await this.userService.checkPermission('Tasks.UnblockTasks.Self');
                return permUnblockAll || (assignedToSelf && permUnblockSelf);
            })(),
        };
    }

    private async buildResumeEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        return {
            label: 'comp.task-list-item.action.resume',
            onClick: async ($event): Promise<void> => {
                if ($event) $event.stopImmediatePropagation();
                await this.resumeTask(task.id);
                this.showStatusChangeNotification('RESUME');
            },
            icon: 'symbol-resume',
            iconStyle: 'outlined',
            disabled: this.hasActiveTaskSync(),
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'DONE') return false;
                const permResumeAll = await this.userService.checkPermission('Tasks.ResumeTasks.All');
                const permResumeSelf = await this.userService.checkPermission('Tasks.ResumeTasks.Self');
                return permResumeAll || (assignedToSelf && permResumeSelf);
            })(),
        };
    }

    private async buildShareEntityAction(task: ITask): Promise<FilterableEntityAction> {
        return {
            label: 'comp.entity-share-popup.share',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                this.domService.appendComponentTo<EntitySharePopupComponent>(
                    'overlay-container',
                    EntitySharePopupComponent,
                    {
                        entity: task,
                        entityType: 'TASK',
                    }
                );
            },
            icon: 'icon-share',
            iconStyle: 'outlined',
            disabled: false,
            applicable: await this.userService.checkPermission('Tasks.ShareTasks'),
        };
    }

    private async buildFinishEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        const hasUnfinishedRequiredForms = (task.forms ?? []).some((form) => form.required && !form.submissionId);
        return {
            label: 'comp.task-list-item.action.finish',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                await this.finishTask(task.id);
                if (
                    await firstValueFrom(
                        this.controlBarService.controlRoute.pipe(map((route) => route.mode === 'DETAIL'))
                    )
                ) {
                    this.controlBarService.popRoute();
                } else {
                    this.showStatusChangeNotification('FINISH');
                }
            },
            icon: 'icon-check',
            disabled: hasUnfinishedRequiredForms || (task.status === 'TODO' && this.hasActiveTaskSync()),
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'IN_PROGRESS' && task.status !== 'TODO' && task.status !== 'INSPECTION_APPROVED')
                    return false;
                const permFinishAll = await this.userService.checkPermission('Tasks.FinishTasks.All');
                const permFinishSelf = await this.userService.checkPermission('Tasks.FinishTasks.Self');
                return permFinishAll || (assignedToSelf && permFinishSelf);
            })(),
        };
    }

    private async buildApproveInspectionEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        return {
            label: 'comp.task-list-item.action.approveInspection',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                await this.approveTaskInspection(task.id);
            },
            disabled: false,
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'TO_BE_INSPECTED') return false;
                return (
                    userId === task.inspectorId && (await this.userService.checkPermission('Tasks.ApproveInspection'))
                );
            })(),
        };
    }

    private async buildRejectInspectionEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        return {
            label: 'comp.task-list-item.action.rejectInspection',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                await this.rejectTaskInspection(task.id);
            },
            disabled: false,
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'TO_BE_INSPECTED') return false;
                return (
                    userId === task.inspectorId && (await this.userService.checkPermission('Tasks.RejectInspection'))
                );
            })(),
        };
    }

    private async buildEditEntityAction(task: ITask): Promise<FilterableEntityAction> {
        return {
            label: 'comp.issue-list-item.action.edit',
            onClick: async (): Promise<void> => {
                const controlRoute: ReportControlRoute = {
                    mode: 'REPORT',
                    reportMode: 'TASK',
                    updateId: task.id,
                    linkedEntity: task.issue,
                    step: 'LOCATION',
                    subjectType: this.tenantService.getSubjectTypeForIdSync(task.subjectTypeId),
                    taskType: this.getTaskTypeForIdSync(task.typeId),
                    description: task.description,
                    location: task.location,
                    linkedPOI: task.poiId ? this.poiService.getPOIForIdSync(task.poiId) : undefined,
                    assignee: await firstValueFrom(this.userService.getUser(task.assigneeId)),
                    finishBefore: task.finishBeforeDate,
                    pickUpAfter: task.pickupAfterDate,
                    navigateOnSubmitted: this.router.url.includes('history') ? this.router.url : undefined,
                };
                if (this.router.url.startsWith('/map')) {
                    this.controlBarService.pushRoute(controlRoute);
                } else {
                    this.router.navigate(['/map'], { state: { controlRoute } });
                }
            },
            icon: 'icon-edit',
            iconStyle: 'outlined',
            disabled: !['TODO', 'IN_PROGRESS', 'BLOCKED'].includes(task.status),
            applicable: await (async (): Promise<boolean> => {
                const permManageAll = await this.userService.checkPermission('Tasks.ManageTasks.All');
                const permManageTeams = await this.userService.checkPermission('Tasks.ManageTasks.Teams');
                return permManageAll || (permManageTeams && (await this.isTaskAssignedToMyTeam(task)));
            })(),
        };
    }

    private async canUndoPickUp(assignedToSelf: boolean): Promise<boolean> {
        const permUndoPickUpAll = await this.userService.checkPermission('Tasks.DropTasks.All');
        const permUndoPickUpSelf = await this.userService.checkPermission('Tasks.DropTasks.Self');
        return permUndoPickUpAll || (assignedToSelf && permUndoPickUpSelf);
    }

    private async navigateAfterPickup(taskId: string): Promise<void> {
        const isListView = await firstValueFrom(
            this.controlBarService.controlRoute.pipe(
                take(1),
                map((route) => route.mode === 'LIST')
            )
        );
        if (isListView) {
            this.controlBarService.pushRoute({
                mode: 'DETAIL',
                entityType: 'TASK',
                entityId: taskId,
            });
        }
    }

    private async isTaskAssignedToMyTeam(task: ITask): Promise<boolean> {
        if (!task.teamId) return false;
        const teamIds = await firstValueFrom(this.teamService.myTeamIds$);
        return isTaskAssignedToTeam(task, teamIds);
    }

    private buildSubmitRequiredFormAction(
        task: ITask,
        updateTaskCallback?: (updatedTask: ITask) => void
    ): FilterableEntityAction {
        return {
            label: 'comp.task-list-item.action.submitRequiredForms',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                return this.navigateToSubmissionOrOpenForm(
                    task,
                    (updatedTask: ITask) => {
                        this.localUpdate(updatedTask);
                        updateTaskCallback?.(updatedTask);
                    },
                    task.forms?.find((f) => f.required && !f.submissionId).id
                );
            },
            icon: 'ballot',
            iconStyle: 'outlined',
            applicable: task.forms?.some((f) => f.required && !f.submissionId) ?? false,
        };
    }

    private async buildPickUpEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        return {
            label: 'comp.task-list-item.action.pickup',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                const canUndo = await this.canUndoPickUp(assignedToSelf);
                await this.pickUpTask(task.id);
                await this.navigateAfterPickup(task.id);
                if (canUndo) {
                    const snackbar = this.domService.appendComponentTo<SnackbarComponent>(
                        'overlay-container',
                        SnackbarComponent,
                        {
                            text: this.translate.instant('comp.task-list-item.snackbar.pickup.onPickup'),
                            actions: [
                                {
                                    label: this.translate.instant('comp.task-list-item.snackbar.pickup.dismissAction'),
                                    onClick: (): void => snackbar.remove(),
                                },
                                {
                                    label: this.translate.instant('comp.task-list-item.snackbar.pickup.undoAction'),
                                    primary: true,
                                    onClick: async (): Promise<void> => {
                                        // Close snackbar
                                        snackbar.remove();
                                        // Drop the task again
                                        await this.dropTask(task.id);
                                        // Pop control route if we are currently on detail view
                                        const detailViewActive = await firstValueFrom(
                                            this.controlBarService.controlRoute.pipe(
                                                take(1),
                                                map((route) => route.mode === 'DETAIL')
                                            )
                                        );
                                        if (detailViewActive) this.controlBarService.popRoute();
                                        // Show drop confirmation
                                        this.domService.appendComponentTo<SnackbarComponent>(
                                            'overlay-container',
                                            SnackbarComponent,
                                            {
                                                text: this.translate.instant(
                                                    'comp.task-list-item.snackbar.pickup.undoConfirm'
                                                ),
                                            }
                                        );
                                    },
                                },
                            ],
                        }
                    );
                }
            },
            icon: 'play_arrow',
            iconStyle: 'outlined',
            disabled: this.hasActiveTaskSync(),
            applicable: await (async (): Promise<boolean> => {
                if (task.status !== 'TODO') return false;
                const permPickUpAll = await this.userService.checkPermission('Tasks.PickUpTasks.All');
                const permPickUpSelf = await this.userService.checkPermission('Tasks.PickUpTasks.Self');
                const permPickUpUnassigned = await this.userService.checkPermission('Tasks.PickUpTasks.Unassigned');
                const permPickUpTeams = await this.userService.checkPermission('Tasks.PickUpTasks.Teams');
                return (
                    permPickUpAll ||
                    (!task.assigneeId && permPickUpUnassigned) ||
                    (permPickUpSelf && assignedToSelf) ||
                    (permPickUpTeams && (await this.isTaskAssignedToMyTeam(task)))
                );
            })(),
        };
    }

    private async buildDropEntityAction(task: ITask, userId: string): Promise<FilterableEntityAction> {
        const assignedToSelf = task.assigneeId && task.assigneeId === userId;
        return {
            label: 'comp.task-list-item.action.drop',
            onClick: ($event): void => {
                $event.stopImmediatePropagation();
                this.dropTask(task.id);
            },
            icon: 'icon-pause',
            iconStyle: 'outlined',
            disabled: false,
            applicable:
                task.status === 'IN_PROGRESS' &&
                (((await this.userService.checkPermission('Tasks.DropTasks.Self')) && assignedToSelf) ||
                    (await this.userService.checkPermission('Tasks.DropTasks.All'))),
        };
    }

    private async buildAssignEntityAction(task: ITask, assignEnabled: boolean): Promise<FilterableEntityAction> {
        const userTeamSelectorTarget = `${TaskService.ASSIGN_POPUP_TARGET_PREFIX}${task.id}` as const;
        const disallowedForTaskStatuses: Array<TaskStatus> = ['DONE', 'CANCELLED'];

        return {
            label: 'comp.task-list-item.action.assign',
            onClick: async ($event): Promise<void> => {
                $event.stopImmediatePropagation();
                await this.router.navigate([this.router.url.split('?')[0], 'assign'], {
                    state: {
                        target: userTeamSelectorTarget,
                        selectedIds: { userId: task.assigneeId || null, teamId: task.teamId || null },
                    },
                });
            },
            icon: 'icon-person',
            iconStyle: 'outlined',
            disabled: false,
            applicable:
                assignEnabled && !disallowedForTaskStatuses.includes(task.status) && (await this.mayAssignTask(task)),
        };
    }

    private async mayAssignTask(task: ITask): Promise<boolean> {
        const permissions: TaskAssignPermissions = {
            assignAll: await this.userService.checkPermission('Tasks.AssignTasks.All'),
            assignTeams: await this.userService.checkPermission('Tasks.AssignTasks.Teams'),
        };
        return canAssignTask(task, permissions, await firstValueFrom(this.teamService.myTeamIds$));
    }

    private async buildAssignInspectionEntityAction(task: ITask): Promise<FilterableEntityAction> {
        return {
            label: 'comp.task-list-item.action.assignInspection',
            onClick: ($event): void => {
                $event.stopImmediatePropagation();
                if (this._assignPopup) {
                    this._assignPopup.remove();
                } else {
                    this.buildAssignPopup(
                        $event,
                        task,
                        (user: IUser) => this.assignInspector(task.id, user.id),
                        () => this.unassignInspector(task.id),
                        (user) => {
                            const permissions = this.roles
                                .filter((role) => user.roleIds.includes(role.id))
                                .reduce(
                                    (acc, role) => [...acc, ...role.permissions.map((permission) => permission.id)],
                                    [] as Array<string>
                                );
                            return (
                                permissions.includes('Tasks.ApproveInspection') ||
                                permissions.includes('Tasks.RejectInspection')
                            );
                        }
                    );
                }
            },
            icon: 'icon-people',
            iconStyle: 'outlined',
            disabled: false,
            applicable: await this.userService.checkPermission('Tasks.AssignTasks.All'),
        };
    }

    private buildAssignPopup(
        $event: MouseEvent,
        task: ITask,
        assignAction: (user: IUser) => Promise<ITask>,
        unassignAction: () => Promise<ITask>,
        filterOnUser: (user: IUser) => boolean = null
    ): void {
        this._assignPopup = this.domService.appendComponentTo<UserSelectPopupComponent>(
            'overlay-container',
            UserSelectPopupComponent,
            {
                padding: 20,
                origin: [$event.clientX, $event.clientY],
                _userFilter: (user: IUser) => (filterOnUser ? of(filterOnUser(user)) : of(true)),
                hideFiltered: false,
            }
        );
        this._assignPopup.component.popupResult
            .pipe(
                tap(() => {
                    if (this._assignPopup) this._assignPopup.remove();
                }),
                switchMap((user: IUser) =>
                    this.socketService.disableUI.pipe(
                        take(1),
                        map((disableUI) => [disableUI, user] as [boolean, IUser])
                    )
                ),
                filter(([disableUI]) => !disableUI),
                switchMap(([, user]) => (user?.id ? assignAction(user) : unassignAction()))
            )
            .subscribe();
        this._assignPopup.on('remove', () => (this._assignPopup = null));
    }

    //
    // Actions
    //

    private showStatusChangeNotification(type: 'FINISH' | 'RESUME' | 'CANCEL'): void {
        const snackbar = this.domService.appendComponentTo<SnackbarComponent>('overlay-container', SnackbarComponent, {
            text: this.translate.instant(`misc.statusChange.task.${type.toLowerCase()}`),
            actions: [
                {
                    label: this.translate.instant('misc.statusChange.actions.dismiss'),
                    onClick: (): void => snackbar.remove(),
                },
            ],
        });
    }

    private static mapTaskTypeToNamedType(type: ITaskType): INamedTaskType {
        return {
            id: type.id,
            name: typeof type.name === 'object' ? `taskType.${type.id}` : type.name,
            iconId: type.iconId,
            archived: type.archived,
        };
    }
}
